/**
 * AiRefinePanel Component
 * Side panel with chat-like interface for AI-powered document refinement.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Send,
  Sparkles,
  Loader2,
  Check,
  Undo2,
  MessageSquare,
  Mic,
  MicOff,
} from 'lucide-react';
import { addApi } from '@/services/addApi';
import { errorMessages } from '@/lib/errors';

// Web Speech API types
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: Event & { error: string }) => void) | null;
  onend: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
  }
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  refinedContent?: string;
  applied?: boolean;
}

interface AiRefinePanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentContent: string;
  onApplyRefinement: (newContent: string) => void;
}

export const AiRefinePanel: React.FC<AiRefinePanelProps> = ({
  isOpen,
  onClose,
  currentContent,
  onApplyRefinement,
}) => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previousContent, setPreviousContent] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const SpeechRecognitionAPI = typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : undefined;
  const speechSupported = !!SpeechRecognitionAPI;

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
      return;
    }

    if (!SpeechRecognitionAPI) return;

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = i18n.language || 'en-GB';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript;
        }
      }
      if (transcript) {
        setInput(prev => prev ? `${prev} ${transcript}` : transcript);
      }
    };

    recognition.onerror = () => {
      stopListening();
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening, stopListening, SpeechRecognitionAPI]);

  // Clean up recognition on unmount or panel close
  useEffect(() => {
    if (!isOpen && isListening) {
      stopListening();
    }
  }, [isOpen, isListening, stopListening]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSend = async () => {
    const instruction = input.trim();
    if (!instruction || isLoading) return;

    if (isListening) stopListening();
    setInput('');
    setError(null);

    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: instruction }]);
    setIsLoading(true);

    try {
      const response = await addApi.refineDocument({
        content: currentContent,
        instruction,
      });

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: response.summary || t('documentEditor.aiRefine.refined'),
          refinedContent: response.content,
          applied: false,
        },
      ]);
    } catch (err) {
      setError(errorMessages.refineDocument(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = (index: number) => {
    const msg = messages[index];
    if (!msg?.refinedContent) return;

    setPreviousContent(currentContent);
    onApplyRefinement(msg.refinedContent);

    setMessages(prev =>
      prev.map((m, i) => (i === index ? { ...m, applied: true } : m))
    );
  };

  const handleRevert = () => {
    if (previousContent !== null) {
      onApplyRefinement(previousContent);
      setPreviousContent(null);

      // Reset applied flags
      setMessages(prev =>
        prev.map(m => ({ ...m, applied: false }))
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="w-80 sm:w-96 border-l border-slate-200 bg-slate-50 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-900">
            {t('documentEditor.aiRefine.title')}
          </h3>
        </div>
        <div className="flex items-center gap-1">
          {previousContent !== null && (
            <button
              onClick={handleRevert}
              title={t('documentEditor.aiRefine.revert')}
              className="p-1.5 rounded text-amber-600 hover:bg-amber-50 transition-colors"
            >
              <Undo2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 max-w-[220px] mx-auto">
              {t('documentEditor.aiRefine.emptyState')}
            </p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                msg.role === 'user'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-700'
              }`}
            >
              <p className="whitespace-pre-wrap">{msg.content}</p>
              {msg.refinedContent && !msg.applied && (
                <button
                  onClick={() => handleApply(i)}
                  className="mt-2 flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded text-xs font-medium hover:bg-emerald-100 transition-colors"
                >
                  <Check className="w-3 h-3" />
                  {t('documentEditor.aiRefine.apply')}
                </button>
              )}
              {msg.applied && (
                <span className="mt-2 flex items-center gap-1 text-xs text-emerald-600">
                  <Check className="w-3 h-3" />
                  {t('documentEditor.aiRefine.applied')}
                </span>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {t('documentEditor.aiRefine.loading')}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-600">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-slate-200 bg-white p-3">
        {isListening && (
          <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-red-50 border border-red-200 rounded-lg">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
            </span>
            <span className="text-xs text-red-600 font-medium">
              {t('documentEditor.aiRefine.listening')}
            </span>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isListening
              ? t('documentEditor.aiRefine.speakNow')
              : t('documentEditor.aiRefine.inputPlaceholder')
            }
            rows={2}
            className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg resize-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            disabled={isLoading}
          />
          {speechSupported && (
            <button
              onClick={toggleListening}
              disabled={isLoading}
              title={isListening
                ? t('documentEditor.aiRefine.stopListening')
                : t('documentEditor.aiRefine.startListening')
              }
              className={`p-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isListening
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="p-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiRefinePanel;
